import type {
  DeprecatedSchema,
  DescribedSchema,
  ForeignKeySchema,
  ForeignKeyPathSchema,
  ForeignKeyStringSchema,
  ForeignKeyTableSchema,
  ForeignKeyHashTableSchema,
  ForeignKeyHashJoinTableSchema,
  PrimaryKeySchema,
  PrimaryKeyTableSchema,
  TypeParameterSchema,
  TypeReferenceSchema,
} from "./schema";
import {
  type Schema,
  type AnySchema,
  type NullishSchema,
  type BooleanSchema,
  type NumberSchema,
  type StringSchema,
  type ArraySchema,
  type ObjectSchema,
  type UnionSchema,
  type EnumSchema,
  type TupleSchema,
  type LiteralSchema,
  type TypeIdentifierSchema,
  type InterfaceSchema,
  type PartialSchema,
  type PickSchema,
  type OmitSchema,
  type ManualSchema,
  type IntersectionSchema,
  type MappedObjectSchema,
  type ObjectPropertySchema,
  type TypedArraySchema,
  type NominalNumberSchema,
  type DeletedSchema,
  type MovedSchema,
} from "./schema";

export class SchemaVisitorBase {
  visit(schema: Schema): void {
    switch (schema.kind) {
      case "PRIMARY_KEY":
        this.visitPrimaryKey(schema);
        break;
      case "PRIMARY_KEY_TABLE":
        this.visitPrimaryKeyTable(schema);
        break;
      case "FOREIGN_KEY":
        this.visitForeignKey(schema);
        break;
      case "FOREIGN_KEY_PATH":
        this.visitForeignKeyPath(schema);
        break;
      case "FOREIGN_KEY_STRING":
        this.visitForeignKeyString(schema);
        break;
      case "FOREIGN_KEY_TABLE":
        this.visitForeignKeyTable(schema);
        break;
      case "FOREIGN_KEY_HASH_TABLE":
        this.visitForeignKeyHashTable(schema);
        break;
      case "FOREIGN_KEY_HASH_JOIN_TABLE":
        this.visitForeignKeyHashJoinTable(schema);
        break;
      case "ANY":
        this.visitAny(schema);
        break;
      case "NULLISH":
        this.visitNullish(schema);
        break;
      case "BOOLEAN":
        this.visitBoolean(schema);
        break;
      case "NUMBER":
        this.visitNumber(schema);
        break;
      case "STRING":
        this.visitString(schema);
        break;
      case "ARRAY":
        this.visitArray(schema);
        break;
      case "OBJECT":
        this.visitObject(schema);
        break;
      case "UNION":
        this.visitUnion(schema);
        break;
      case "ENUM":
        this.visitEnum(schema);
        break;
      case "TUPLE":
        this.visitTuple(schema);
        break;
      case "LITERAL":
        this.visitLiteral(schema);
        break;
      case "IDENTIFIER":
        this.visitTypeIdentifier(schema);
        break;
      case "INTERFACE":
        this.visitInterface(schema);
        break;
      case "PARTIAL":
        this.visitPartial(schema);
        break;
      case "PICK":
        this.visitPick(schema);
        break;
      case "OMIT":
        this.visitOmit(schema);
        break;
      case "MANUAL":
        this.visitManual(schema);
        break;
      case "INTERSECTION":
        this.visitIntersection(schema);
        break;
      case "MAPPED_OBJECT":
        this.visitMappedObject(schema);
        break;
      case "OBJECT_PROPERTY":
        this.visitObjectProperty(schema);
        break;
      case "TYPED_ARRAY":
        this.visitTypedArray(schema);
        break;
      case "TYPE_REFERENCE":
        this.visitTypeReference(schema);
        break;
      case "TYPE_PARAMETER":
        this.visitTypeParameter(schema);
        break;
      case "NOMINAL_NUMBER":
        this.visitNominalNumber(schema);
        break;
      case "DELETED":
        this.visitDeleted(schema);
        break;
      case "MOVED":
        this.visitMoved(schema);
        break;
      case "DEPRECATED":
        this.visitDeprecated(schema);
        break;
      case "DESCRIBED":
        this.visitDescribed(schema);
        break;
    }
  }

  visitTypeReference(schema: TypeReferenceSchema): void {
    this.visit(schema.targetType);
    Object.values(schema.typeArgs).forEach((type) => this.visit(type));
  }

  visitTypeParameter(_schema: TypeParameterSchema): void {}

  visitPrimaryKey(_schema: PrimaryKeySchema): void {}

  visitPrimaryKeyTable(_schema: PrimaryKeyTableSchema): void {
    this.visit(_schema.valueType);
  }

  visitForeignKey(_schema: ForeignKeySchema): void {}
  visitForeignKeyPath(_schema: ForeignKeyPathSchema): void {}
  visitForeignKeyString(_schema: ForeignKeyStringSchema): void {}
  visitForeignKeyTable(schema: ForeignKeyTableSchema): void {
    this.visit(schema.valueType);
  }

  visitForeignKeyHashTable(schema: ForeignKeyHashTableSchema): void {
    this.visit(schema.valueType);
  }

  visitForeignKeyHashJoinTable(schema: ForeignKeyHashJoinTableSchema): void {
    this.visit(schema.valueType);
  }

  visitAny(_schema: AnySchema): void {}

  visitNullish(_schema: NullishSchema): void {}

  visitBoolean(_schema: BooleanSchema): void {}

  visitNumber(_schema: NumberSchema): void {}

  visitString(_schema: StringSchema): void {}

  visitArray(schema: ArraySchema): void {
    this.visit(schema.arrayType);
  }

  visitObject(schema: ObjectSchema): void {
    this.visit(schema.valueType);
  }

  visitUnion(schema: UnionSchema): void {
    schema.unionTypes.forEach((type) => this.visit(type));
  }

  visitEnum(_schema: EnumSchema): void {}

  visitTuple(schema: TupleSchema): void {
    schema.tupleTypes.forEach((type) => this.visit(type));
  }

  visitLiteral(_schema: LiteralSchema): void {}

  visitTypeIdentifier(_schema: TypeIdentifierSchema): void {}

  visitInterface(schema: InterfaceSchema): void {
    Object.values(schema.fields).forEach((schema) => this.visit(schema));
  }

  visitPartial(schema: PartialSchema): void {
    this.visit(schema.valueType);
  }

  visitPick(schema: PickSchema): void {
    this.visit(schema.valueType);
    this.visit(schema.fields);
  }

  visitOmit(schema: OmitSchema): void {
    this.visit(schema.valueType);
    this.visit(schema.fields);
  }

  visitManual(_schema: ManualSchema): void {}

  visitIntersection(schema: IntersectionSchema): void {
    schema.intersectionTypes.forEach((type) => this.visit(type));
  }

  visitMappedObject(schema: MappedObjectSchema): void {
    this.visit(schema.indexType);
    this.visit(schema.valueType);
  }

  visitObjectProperty(schema: ObjectPropertySchema): void {
    this.visit(schema.schema);
  }

  visitTypedArray(_schema: TypedArraySchema): void {}

  visitNominalNumber(_schema: NominalNumberSchema): void {}

  visitDeleted(schema: DeletedSchema): void {
    this.visit(schema.schema);
  }
  visitMoved(schema: MovedSchema): void {
    this.visit(schema.schema);
  }
  visitDeprecated(schema: DeprecatedSchema): void {
    this.visit(schema.schema);
  }
  visitDescribed(schema: DescribedSchema): void {
    this.visit(schema.schema);
  }
}
